import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LabelId } from '@this+that/models';
import log from 'loglevel';

import { modifyEmailLabelsUnified } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import { useUserCommunicationsStore } from '../../../store';
import { useCustomToast } from '../../useCustomToast';

export const useModifyEmailLabels = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { successMessage, errorMessage } = useCustomToast();

  const modifyLabels = useCallback(
    async (
      messageIds: string[],
      selectedLabelIds: string[],
      onSuccess?: () => void,
    ) => {
      const { updateUserCommunicationLabels, getUserCommunicationByMessageId } =
        useUserCommunicationsStore.getState();
      if (messageIds.length === 0) {
        log.error('No messageIds provided');
        errorMessage(t('inbox.labels.modifyLabelsError'));
        return;
      }

      const firstUserCommunication = getUserCommunicationByMessageId(
        messageIds[0],
      );

      if (!firstUserCommunication) {
        log.error(
          `UserCommunication with messageId ${messageIds[0]} not found`,
        );
        errorMessage(t('inbox.labels.modifyLabelsError'));
        return;
      }

      if (!firstUserCommunication.userEmailAddress) {
        log.error('No user email address found in communication');
        errorMessage(t('inbox.labels.modifyLabelsError'));
        return;
      }

      const currentLabelIds = (firstUserCommunication.labelIds || []).filter(
        (id) =>
          !Object.values(LabelId).includes(id as LabelId) ||
          id === LabelId.IMPORTANT ||
          id === LabelId.STARRED,
      );

      const addLabelIds = selectedLabelIds.filter(
        (id) => !currentLabelIds.includes(id as LabelId),
      );
      const removeLabelIds = currentLabelIds.filter(
        (id) => !selectedLabelIds.includes(id as LabelId),
      );

      if (addLabelIds.length === 0 && removeLabelIds.length === 0) {
        log.info('No label changes detected');
        return;
      }

      setIsLoading(true);

      const rollbackData = messageIds.map((messageId) => {
        const uc = getUserCommunicationByMessageId(messageId);
        return {
          messageId,
          previousLabelIds: uc?.labelIds || [],
        };
      });

      messageIds.forEach((messageId) => {
        const userCommunication = getUserCommunicationByMessageId(messageId);
        if (userCommunication) {
          const newLabelIds = [
            ...(userCommunication.labelIds || []).filter(
              (id) =>
                Object.values(LabelId).includes(id as LabelId) &&
                id !== LabelId.IMPORTANT &&
                id !== LabelId.STARRED,
            ),
            ...selectedLabelIds,
          ];
          updateUserCommunicationLabels(messageId, newLabelIds);
        }
      });

      try {
        await modifyEmailLabelsUnified({
          emailAddress: firstUserCommunication.userEmailAddress,
          platform: firstUserCommunication.platform!,
          messageIds,
          addLabelIds,
          removeLabelIds,
        });

        log.info(
          `Labels modified successfully for ${messageIds.length} message(s)`,
        );
        successMessage(
          messageIds.length === 1
            ? t('inbox.labels.modifyLabelsSuccess')
            : t('inbox.labels.modifyLabelsMultipleSuccess', {
                count: messageIds.length,
              }),
        );
        onSuccess?.();
      } catch (error) {
        log.error(`Failed to modify labels for messageIds:`, error);
        rollbackData.forEach(({ messageId, previousLabelIds }) => {
          updateUserCommunicationLabels(messageId, previousLabelIds);
        });
        errorMessage(t('inbox.labels.modifyLabelsError'));
      } finally {
        setIsLoading(false);
      }
    },
    [successMessage, errorMessage, t],
  );

  return {
    modifyLabels,
    isLoading,
  };
};
